import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import {useState} from 'react';
import AurButton from '../common/aur-button/AurButton';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../eca-doc-list/eca-save-search/EcaSaveSearch';
import AurLocationTreeView from '../eca-doc-list/eca-save-search/AurLocationTreeView';
import AurIconButton from '../common/aur-button/AurIconButton';
import AurIcon, {IconNames} from '../common/aur-icon/AurIcon';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  updateSaveSearchDetails,
  updateSearchGroupDetails,
} from '../../api/EcaServices';
import {SavedSearchDetails} from './SavedSearchPanel';
import {AurLoadingButton} from '../common/aur-loading-button/AurLoadingButton';
import {extractSavedSearchId} from '../../utils/utils';

interface MoveSavedSearchGroupPopupProps {
  isOpen: boolean;
  onClose: () => void;
  itemsList: TreeViewBaseItem<ExtendedTreeItemProps>[];
  getChildren?: (
    id: string,
    directory_structure_ids?: string
  ) => Promise<TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined>;
  defaultExpandedIds?: string[];
  selectedNodeToMove: TreeViewBaseItem<ExtendedTreeItemProps> | null;
  savedSearchDetails: SavedSearchDetails;
  onSuccessAction: (message: string) => void;
  onActionFailed: (message: string) => void;
  onMoveComplete: (
    preExpandedIds: string[],
    selectedNodeToMove: TreeViewBaseItem<ExtendedTreeItemProps>,
    type: string
  ) => void;
  isTemplate?: boolean;
  templateOps?: {
    onMoveGroupTemplate: (
      groupId: string,
      reqBody: {name: string; parentGroupId: string}
    ) => any;
    onMoveSearchTemplate: (
      savedSearchId: string,
      reqBody: {name: string; groupId: string}
    ) => any;
  };
}

const MoveSavedSearchGroupPopup = (props: MoveSavedSearchGroupPopupProps) => {
  const {
    isOpen,
    onClose: onModalClose,
    itemsList,
    getChildren,
    defaultExpandedIds,
    selectedNodeToMove,
    savedSearchDetails,
    onSuccessAction,
    onActionFailed,
    onMoveComplete,
    isTemplate,
    templateOps,
  } = props;

  const [expandedIds] = useState(defaultExpandedIds);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNode, setSelectedNode] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>(null);
  const handleSelectedItem = (
    selectedNode: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    setSelectedNode(selectedNode);
  };

  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );

  const onClose = () => {
    setSelectedNode(null);
    onModalClose && onModalClose();
  };

  const moveSearch = async (params: string) => {
    if (selectedNodeToMove && selectedNode) {
      try {
        const results = await updateSaveSearchDetails(
          params,
          extractSavedSearchId(selectedNodeToMove.id),
          {
            ...savedSearchDetails,
            groupId: extractSavedSearchId(selectedNode.id),
          }
        );
        if (results.status === 200) {
          onMoveComplete(
            selectedNode?.directory_structure_ids?.split('/') ?? [],
            selectedNodeToMove,
            'SavedSearch'
          );
          onClose();
          onSuccessAction(
            t('eca.savedSearchManagement.groupOrSavedSearchMoved', {
              movedGroupOrSavedSearchName: selectedNodeToMove.label,
            })
          );
        }
      } catch {
        onClose();
        onActionFailed(t('eca.savedSearchManagement.savedSearchMovedError'));
      }
    }
  };

  const moveSavedSearchTemplate = async () => {
    if (selectedNodeToMove && selectedNode) {
      try {
        const reqBody: {name: string; groupId: string} = {
          name: savedSearchDetails.name,
          groupId: extractSavedSearchId(selectedNode.id),
        };

        const savedSearchId = extractSavedSearchId(selectedNodeToMove.id);
        const results = await templateOps?.onMoveSearchTemplate(
          savedSearchId,
          reqBody
        );
        if (results.status === 200) {
          onMoveComplete(
            selectedNode?.directory_structure_ids?.split('/') ?? [],
            selectedNodeToMove,
            'SavedSearch'
          );
          onClose();
          onSuccessAction(
            t('eca.savedSearchManagement.groupOrSavedSearchMoved', {
              movedGroupOrSavedSearchName: selectedNodeToMove.label,
            })
          );
        }
      } catch {
        onClose();
        onActionFailed(t('eca.savedSearchManagement.savedSearchMovedError'));
      }
    }
  };

  const moveGroup = async (params: string) => {
    if (selectedNodeToMove && selectedNode) {
      try {
        const results = await updateSearchGroupDetails(
          params,
          extractSavedSearchId(selectedNodeToMove.id),
          {
            name: selectedNodeToMove.label,
            parentGroupId: extractSavedSearchId(selectedNode.id),
          }
        );
        if (results.status === 200) {
          onMoveComplete(
            selectedNode?.directory_structure_ids?.split('/') ?? [],
            selectedNodeToMove,
            'SavedSearchGroup'
          );
          onClose();
          onSuccessAction(
            t('eca.savedSearchManagement.groupOrSavedSearchMoved', {
              movedGroupOrSavedSearchName: selectedNodeToMove.label,
            })
          );
        }
      } catch {
        onClose();
        onActionFailed(t('eca.savedSearchManagement.searchGroupMovedError'));
      }
    }
  };

  /**This fn handles placing of group templates */
  const moveSavedSearchGroupTemplate = async () => {
    if (selectedNodeToMove && selectedNode) {
      try {
        const groupId = extractSavedSearchId(selectedNodeToMove.id);      
        const results = await templateOps?.onMoveGroupTemplate(groupId, {
          name: selectedNodeToMove.label,
          parentGroupId: extractSavedSearchId(selectedNode.id),
        });
        if (results.status === 200) {
          onMoveComplete(
            selectedNode?.directory_structure_ids?.split('/') ?? [],
            selectedNodeToMove,
            'SavedSearchGroup'
          );
          onClose();
          onSuccessAction(
            t('eca.savedSearchManagement.groupOrSavedSearchMoved', {
              movedGroupOrSavedSearchName: selectedNodeToMove.label,
            })
          );
        }
      } catch {
        onClose();
        onActionFailed(t('eca.savedSearchManagement.searchGroupMovedError'));
      }
    }
  };

  const OnMove = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    setIsLoading(true);
    if (selectedNodeToMove?.fileType === 'folder') {
      isTemplate
        ? await moveSavedSearchGroupTemplate()
        : await moveGroup(params);
    } else {
      isTemplate ? await moveSavedSearchTemplate() : await moveSearch(params);
    }
    setIsLoading(false);
  };

  const renderDialogActions = () => {
    return (
      <DialogActions>
        <Grid container className={'dialog-footer'}>
          <Grid item>
            <AurButton
              dataTestId={'cancelRelationshipDialogButton'}
              classNames={'typo-text custom-button cancel-button'}
              type={'exit'}
              size={'Medium'}
              label={t(
                'eca.savedSearchManagement.moveSavedSearchGroupPopUp.buttonCancel'
              )}
              onClick={onClose}
              tabIndex={0}
            />
          </Grid>
          <Grid item>
            <AurLoadingButton
              label={t(
                'eca.savedSearchManagement.moveSavedSearchGroupPopUp.buttonMove'
              )}
              loading={isLoading}
              onClick={OnMove}
              size="Medium"
              buttonType="submit"
              classNames={`typo-text custom-button move-button ${isLoading && 'apply-button-loading'}`}
              disabled={!selectedNode}
              dataTestId="move-search"
              tabIndex={0}
            />
          </Grid>
        </Grid>
      </DialogActions>
    );
  };

  const renderPopupTitle = () => (
    <DialogTitle>
      <Box className="primary-header">
        <Typography
          tabIndex={0}
          className="typo-text headline3_size heading-text title-text"
        >
          {t('eca.savedSearchManagement.moveSavedSearchGroupPopUp.popupTitle')}
        </Typography>
        <AurIconButton
          ariaLabel={t('eca.savedSearchManagement.moveSavedSearchGroupPopUp.buttonClose')}
          size="big"
          icon={<AurIcon icon={IconNames.Close} />}
          onClick={onClose}
          dataTestId="close-button"
        />
      </Box>
      <Box className="location-box">
        {' '}
        <Typography className="typo-text label2_size location-label">
          {t('ecaDocList.saveSearch.location')}
        </Typography>{' '}
      </Box>
    </DialogTitle>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      data-testid="move-saved-search-group-popup"
      className="popup aur-unsaved-changes-popup move-saved-search-group-popup"
    >
      {renderPopupTitle()}
      <DialogContent className={'drawer-treeview-content custom-treeview '}>
        {itemsList.length === 1 ? (
          <AurLocationTreeView
            itemsList={itemsList}
            handleSelectedItem={handleSelectedItem}
            getChildren={getChildren}
            defaultExpandedIds={expandedIds}
            showCheckmark
            selectedNodeToMove={selectedNodeToMove?.id}
          />
        ) : (
          <div className="filtered-saved-search">
            <AurLocationTreeView
              itemsList={itemsList}
              handleSelectedItem={handleSelectedItem}
              getChildren={getChildren}
              defaultExpandedIds={expandedIds}
              showCheckmark
              selectedNodeToMove={selectedNodeToMove?.id}
            />
          </div>
        )}
      </DialogContent>
      {renderDialogActions()}
    </Dialog>
  );
};

export default MoveSavedSearchGroupPopup;
